import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ExpenseApiService } from '../../../core/api-services/expense-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import {
  AllocationPaymentReport,
  MonthlyPaymentRequirement,
  ScholarshipClaimReport,
} from '../../../core/models/expense';
import { Scholarship } from '../../../core/models/scholarship';

/**
 * Financial reports for scholarship administrators: what has been allocated and
 * paid, what must be paid this month, and the claims submitted per scholarship.
 */
@Component({
  selector: 'app-admin-reports',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule],
  templateUrl: './reports.html',
  styleUrl: './reports.scss',
})
export class AdminReportsComponent implements OnInit {
  private readonly api = inject(ExpenseApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);

  readonly allocations = signal<AllocationPaymentReport[]>([]);
  readonly requirement = signal<MonthlyPaymentRequirement | null>(null);
  readonly claimReports = signal<ScholarshipClaimReport[]>([]);

  readonly scholarships = signal<Scholarship[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');

  activeTab: 'allocations' | 'payments' | 'claims' = 'allocations';

  // Filters shared by the reports.
  cycle = '';
  scholarshipId: number | null = null;
  year: number = new Date().getFullYear();
  month: number = new Date().getMonth() + 1;

  readonly months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  ngOnInit(): void {
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => this.scholarships.set([]),
    });

    this.loadAllocations();
  }

  selectTab(tab: 'allocations' | 'payments' | 'claims'): void {
    this.activeTab = tab;

    if (tab === 'allocations') {
      this.loadAllocations();
    } else if (tab === 'payments') {
      this.loadRequirement();
    } else {
      this.loadClaimReports();
    }
  }

  loadAllocations(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getAllocationReport(this.cycle || null, this.scholarshipId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (reports) => this.allocations.set(reports ?? []),
        error: () => this.error.set('The allocation report could not be loaded.'),
      });
  }

  loadRequirement(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getMonthlyPaymentRequirement(this.year, this.month, this.scholarshipId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (report) => this.requirement.set(report),
        error: () => this.error.set('The payment requirement could not be loaded.'),
      });
  }

  loadClaimReports(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getClaimReport(this.cycle || null, this.scholarshipId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (reports) => this.claimReports.set(reports ?? []),
        error: () => this.error.set('The claim report could not be loaded.'),
      });
  }

  money(value: number | null | undefined, currency: string | null | undefined): string {
    return `${(value ?? 0).toFixed(2)} ${currency ?? ''}`.trim();
  }
}
